import { ChargeStatus } from '@/@types/generated'
import { PaymentsService } from '@/@types/generated/services/PaymentsService'
import { Breadcrumb } from '@/components/Atoms/Breadcrumbs'
import Loading from '@/components/Atoms/Loading'
import { Header } from '@/components/ui/header'
import { useAuth } from '@/hooks/useAuth'
import { buildPath } from '@/routes'
import { Suspense, useCallback } from 'react'
import { Outlet, useSearchParams } from 'react-router'
import {
  ChargesProvider,
  ChargesProviderProps,
} from '../hooks/charges-provider'
import { PaymentProcessProvider } from '../hooks/payment-process-provider'
import { PaymentProcessSteps } from './PaymentProcessSteps'

export function PaymentProcessLayout() {
  // Hooks
  const { user } = useAuth()
  const [searchParams] = useSearchParams()

  // List charges
  // This functions differently depending on user type:
  // - Internal users list all charges and can filter further
  // - Non-internal users see only the charges for the selected business entity
  const listCharges: ChargesProviderProps['listCharges'] = useCallback(
    async (req) => {
      const res = await PaymentsService.listCharges(
        req.query,
        req.businessEntityId ||
          searchParams.get('businessEntityId') ||
          undefined,
        [ChargeStatus.OUTSTANDING], // only list outstanding charges on payment flow
        req.createdAt,
        req.limit,
        req.offset,
        req.sortBy,
        req.sortDirection
      )
      return { charges: res.charges ?? [], total: res.total }
    },
    [searchParams]
  )

  // No user yet, show loading
  if (!user) return <Loading />

  return (
    <>
      <Breadcrumb
        label="Make a Payment"
        path={buildPath('/payments/process')}
      />

      <div className="flex flex-col gap-6">
        <Header variant="h1">Make a Payment</Header>

        <ChargesProvider listCharges={listCharges}>
          {/* Business entity always exists here since PaymentsLayout wraps RequireBusinessEntity */}
          <PaymentProcessProvider>
            <PaymentProcessSteps>
              <Suspense fallback={<Loading variant="fullscreen" />}>
                <Outlet />
              </Suspense>
            </PaymentProcessSteps>
          </PaymentProcessProvider>
        </ChargesProvider>
      </div>
    </>
  )
}
